<template>
  <div
    class="flatfinder-leaflet-v1-card"
    :class="{ 'flatfinder-leaflet-v1-card--visible': reference.id }"
  >
    <div
      class="flatfinder-leaflet-v1-card__cover"
      :style="{ backgroundImage: `url(${cover})` }"
    ></div>
    <div class="flatfinder-leaflet-v1-card__content grow-1">
      <h1 class="flatfinder-leaflet-v1-card__title">{{ reference.name }}</h1>
      <div
        v-if="value.data && value.data.description"
        class="flatfinder-leaflet-v1-card__description"
        v-html="value.data.description"
      ></div>
      <div
        v-for="item in properties"
        :key="item.key"
        class="flatfinder-leaflet-v1-card__properties"
      >
        <b class="mr-1">{{ item.label || $tc(item.key) }}</b>
        <span>{{ item.formatted }}</span>
      </div>
      <div class="flatfinder-leaflet-v1-card__price">{{ price }}</div>
      <div v-if="hasPrice && reference.status === 'sale'" class="flatfinder-leaflet-v1-card__fee">
        <template v-if="$path('sale.fee.included', value)">{{
          $t('residentialSaleFeeIncluded', { value: null })
        }}</template>
        <template v-else> + {{ $t('residentialSaleFee').toLowerCase() }} </template>
        <template v-if="fee"> ({{ fee | Currency }})</template>
      </div>
    </div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { Currency, path } from './utils'

export default {
  props: {
    value: Object,
    mouse: Object,
  },
  data() {
    return {
      popper: null,
    }
  },
  watch: {
    mouse: {
      handler() {
        if (!this.popper) return
        this.popper.update()
      },
      deep: true,
    },
  },
  filters: {
    Currency,
  },
  computed: {
    reference() {
      return path('value.reference', this)
    },
    properties() {
      return (path('reference.properties', this) || []).slice(0, 3)
    },
    fee() {
      return path('reference.sale.fee.value', this)
    },
    cover() {
      return path('reference.media.cover.0.url', this)
    },
    hasPrice() {
      return Boolean(path('reference.sale.value', this))
    },
    price() {
      let price = Currency(path('sale.value', this.reference))
      switch (this.reference.status) {
        case 'sale':
          if ([null, undefined].includes(price)) return this.$t('priceOnRequest')
          return price
        case 'reserved':
          return `${this.$t(
            'residentialStatus:' + this.reference.sale.type + '.' + this.reference.status,
          )}${price && !KvassConfig.get('siteSettings.hideReservedPrice') ? ` (${price})` : ''}`
        case 'rent':
          if ([null, undefined].includes(price)) return this.$t('priceOnRequest')
          return `${price} / ${this.$t('multiplicatorMonthly')}.`
        default:
          return this.$t(
            'residentialStatus:' + this.reference.sale.type + '.' + this.reference.status,
          )
      }
    },
    boundingRect() {
      return () => ({
        width: 0,
        height: 0,
        top: this.mouse.y,
        right: this.mouse.x,
        bottom: this.mouse.y,
        left: this.mouse.x,
      })
    },
  },
  mounted() {
    this.popper = createPopper(
      { getBoundingClientRect: this.boundingRect, contextElement: this.$parent.$el },
      this.$el,
      {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 80],
            },
          },
        ],
      },
    )
  },
  beforeDestroy() {
    if (this.popper) this.popper.destroy()
  },
}
</script>

<style lang="scss">
.flatfinder-leaflet-v1-card {
  background-color: white;
  color: $dark;
  box-shadow: 0 10px 10px -10px rgba(black, 0.1);
  border-radius: $border-radius;
  overflow: hidden;
  display: flex;
  z-index: 999;
  pointer-events: none;
  position: absolute;
  font-size: 0.75em;
  opacity: 0;
  transition: opacity 150ms ease-out;

  &--visible {
    opacity: 1;
  }

  &__cover {
    width: 100px;
    background-size: cover;
    background-position: center;
    background-color: css-alpha('primary', 0.07);
  }

  &__description {
    max-width: 200px;
    margin-bottom: 1rem;
  }

  &__title {
    margin: 0 0 1rem 0;
    line-height: 1;
    font-size: 1.4em;
  }

  &__content {
    padding: 1rem;
  }

  &__properties {
    display: flex;
    justify-content: space-between;
    max-width: 250px;
  }

  &__price {
    line-height: 1;
    margin: 1rem 0 0;
    font-size: 1.4em;
    font-weight: bold;
  }

  &__fee {
    opacity: 0.5;
    margin-top: 0.5em;
  }
}
</style>
